import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

// Hero background images – one is chosen at random on each page load.
// To use your own: add images to public/assets/images/hero/ (e.g. hero-1.jpg … hero-6.jpg) and set:
// HERO_IMAGES = ['/assets/images/hero/hero-1.jpg', '/assets/images/hero/hero-2.jpg', ...]
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1529636798458-92182a662888?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1920&q=80',
];

const HeroSection = ({ onJoinFree, onStartJourney }) => {
  const { user } = useAuth();
  const heroImage = useMemo(
    () => HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)],
    []
  );

  return (
    <section className="relative min-h-[600px] md:min-h-[700px] lg:min-h-[800px] flex items-center justify-center overflow-hidden">
      {/* Random hero background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500"
        style={{ backgroundImage: `url(${heroImage})` }}
        aria-hidden
      />
      {/* Dark overlay so all hero text stays readable on any image */}
      <div className="absolute inset-0 bg-black/55" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" aria-hidden />

      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 md:w-48 md:h-48 lg:w-64 lg:h-64 rounded-full bg-primary blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-40 h-40 md:w-56 md:h-56 lg:w-72 lg:h-72 rounded-full bg-secondary blur-3xl animate-pulse delay-1000" />
      </div>

      <div
        className="relative z-10 w-full max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20"
        style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8), 0 2px 12px rgba(0,0,0,0.6)' }}
      >
        <div className="text-center space-y-6 md:space-y-8 lg:space-y-10">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-2 text-white">
              {user ? 'Welcome Back! 👋' : 'Welcome to Bamchik 👋'}
            </h1>
            <p className="text-sm md:text-base text-white/95">
              {user
                ? "Here's what's happening with your profile today"
                : 'Private, discreet connections — join women free or start your journey as a member.'}
            </p>
          </div>

          <div className="space-y-3 md:space-y-4">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-white">
              Your Secret Space for Discreet Connections
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-white/95 max-w-2xl mx-auto leading-relaxed">
              Connect anonymously, chat privately, and explore relationships without judgment. Your privacy is our priority.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 pt-4 md:pt-6">
            <Button
              variant="default"
              size="lg"
              iconName="UserPlus"
              iconPosition="left"
              onClick={onJoinFree}
              className="w-full sm:w-auto min-w-[200px] md:min-w-[240px] shadow-warm-lg hover:shadow-warm-xl transition-smooth"
            >
              Join Free (Women)
            </Button>
            <Button
              variant="secondary"
              size="lg"
              iconName="Sparkles"
              iconPosition="left"
              onClick={onStartJourney}
              className="w-full sm:w-auto min-w-[200px] md:min-w-[240px] shadow-warm-lg hover:shadow-warm-xl transition-smooth"
            >
              Start Your Journey (Men)
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 lg:gap-8 pt-6 md:pt-8 text-sm md:text-base text-white/95">
            <div className="flex items-center gap-2">
              <Icon name="ShieldCheck" size={20} color="var(--color-success)" />
              <span>100% Anonymous</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={20} color="var(--color-success)" />
              <span>Secure & Private</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Users" size={20} color="var(--color-success)" />
              <span>10,000+ Active Members</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;